import { useState, useEffect } from 'react';

// Global cache to track loaded images across component mounts
const imageCache = new Map();

/**
 * Check if an image is already cached in the browser
 * @param {string} src - Image source
 * @returns {Promise<boolean>} - True if image is cached and ready
 */
const checkImageCache = (src) => {
  return new Promise((resolve) => {
    // Check our in-memory cache first
    if (imageCache.has(src)) {
      resolve(true);
      return;
    }

    // Check browser cache by creating an image and checking if it's complete
    const img = new Image();
    let resolved = false;
    
    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
    
    const onLoad = () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        imageCache.set(src, true);
        resolve(true);
      }
    };
    
    const onError = () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    };

    // Add event listeners before setting src
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
    
    // Set src to trigger load check
    img.src = src;
    
    // If image is already in browser cache, it will be complete immediately
    // or within a very short time (same event loop tick)
    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      // Image is cached and ready
      onLoad();
      return;
    }
    
    // Give it a very short timeout - cached images load almost instantly
    // If it doesn't load within 50ms, assume it's not cached
    setTimeout(() => {
      if (!resolved) {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          // Loaded during the timeout - it was cached
          onLoad();
        } else {
          // Not cached - normal loading will handle it
          resolved = true;
          cleanup();
          resolve(false);
        }
      }
    }, 50);
  });
};

/**
 * Custom hook for preloading images with loading states
 * @param {string|string[]} src - Image source or array of sources
 * @param {Object} options - Configuration options
 * @returns {Object} - Loading state and loaded status
 */
export const useImagePreloader = (src, options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wasCached, setWasCached] = useState(false);
  const [error, setError] = useState(null);

  const {
    priority = false, // High priority images load immediately
    lazy = true,      // Lazy load by default
    timeout = 10000   // 10 second timeout
  } = options;

  useEffect(() => {
    if (!src) return;

    const sources = Array.isArray(src) ? src : [src];
    if (sources.length === 0) return;

    setError(null);

    // First, check if images are already cached
    const cacheChecks = sources.map(source => checkImageCache(source));
    
    Promise.all(cacheChecks).then(cacheResults => {
      const allCached = cacheResults.every(cached => cached === true);
      
      if (allCached) {
        // All images are cached - skip loading state and show immediately
        setWasCached(true);
        setIsLoaded(true);
        setIsLoading(false);
        return;
      }
      
      // Not cached - mark as not cached
      setWasCached(false);
      
      // At least one image needs loading - show loading state
      setIsLoading(true);

      // Create timeout
      const timeoutId = setTimeout(() => {
        setError(new Error('Image loading timeout'));
        setIsLoading(false);
      }, timeout);

      // Preload all images
      const imagePromises = sources.map((source, index) => {
        // If already cached, resolve immediately
        if (cacheResults[index]) {
          return Promise.resolve(source);
        }
        
        return new Promise((resolve, reject) => {
          const img = new Image();
          
          img.onload = () => {
            // Add small delay for smooth transition
            setTimeout(() => {
              imageCache.set(source, true);
              resolve(source);
            }, 50);
          };
          
          img.onerror = () => reject(new Error(`Failed to load image: ${source}`));
          
          // Set priority loading hint
          if (priority) {
            img.fetchPriority = 'high';
          }
          
          img.src = source;
        });
      });

      Promise.allSettled(imagePromises)
        .then(results => {
          clearTimeout(timeoutId);
          
          const hasFailures = results.some(result => result.status === 'rejected');
          if (hasFailures && !priority) {
            // Log errors but don't fail completely for non-priority images
            console.warn('Some images failed to load:', 
              results.filter(r => r.status === 'rejected').map(r => r.reason)
            );
          }
          
          setIsLoaded(true);
          setIsLoading(false);
        })
        .catch(err => {
          clearTimeout(timeoutId);
          setError(err);
          setIsLoading(false);
        });
    });

    // No cleanup needed for cache checks as they're async
  }, [src, priority, timeout]);

  return { isLoaded, isLoading, wasCached, error };
};

/**
 * Hook specifically for background image preloading
 */
export const useBackgroundPreloader = (src) => {
  return useImagePreloader(src, { 
    priority: true, 
    lazy: false, 
    timeout: 8000 
  });
};
